// @ts-strict-ignore
import { createContext, FC, ReactNode, useContext } from 'react'

export type CatalogUrlState = {
	pathname: string
	search: string
	resolvedUrl: string
}

export type CatalogUrlContextValue = CatalogUrlState & {
	updateResolvedUrl: (nextResolvedUrl: string) => void
}

const CatalogUrlContext = createContext<CatalogUrlContextValue | null>(null)

export const useCatalogUrl = (): CatalogUrlContextValue | null => useContext(CatalogUrlContext)

export const getCatalogUrlParts = (resolvedUrl: string): CatalogUrlState => {
	if (!resolvedUrl) {
		return {
			pathname: '',
			search: '',
			resolvedUrl: ''
		}
	}

	if (resolvedUrl.startsWith('http://') || resolvedUrl.startsWith('https://')) {
		try {
			const normalizedUrl = new URL(resolvedUrl)
			return {
				pathname: normalizedUrl.pathname,
				search: normalizedUrl.search.slice(1),
				resolvedUrl: `${normalizedUrl.pathname}${normalizedUrl.search}`
			}
		} catch {}
	}

	const [pathname = '', rawSearch = ''] = resolvedUrl.split('?')
	const search = rawSearch.startsWith('?') ? rawSearch.slice(1) : rawSearch

	return {
		pathname,
		search,
		resolvedUrl: `${pathname}${search ? `?${search}` : ''}`
	}
}

export const normalizeResolvedUrl = (resolvedUrl: string): string => {
	const { pathname, search } = getCatalogUrlParts(resolvedUrl)

	if (!pathname) {
		return search ? `?${search}` : ''
	}

	// Enforce the site-wide trailing slash (next.config `trailingSlash: true`). The backend sef-url
	// for a filter comes back WITHOUT it, and we apply URLs via history.replaceState (updateResolvedUrl),
	// which bypasses the Next router — so `trailingSlash` never kicks in and the address bar would lose
	// its closing slash (…/filter/foo-is-bar instead of …/filter/foo-is-bar/).
	const pathnameWithSlash = pathname.endsWith('/') ? pathname : `${pathname}/`

	return `${pathnameWithSlash}${search ? `?${search}` : ''}`
}

type CatalogUrlProviderProps = CatalogUrlContextValue & {
	children: ReactNode
}

export const CatalogUrlProvider: FC<CatalogUrlProviderProps> = ({ children, ...value }) => {
	return <CatalogUrlContext.Provider value={value}>{children}</CatalogUrlContext.Provider>
}
